"use client";

import type { ReactNode } from "react";
import type { CurrencyInfo } from "@/lib/currency-format";
import type { LocalizedText, OrderItemVariantOptions } from "@/lib/validation/json";
import { useRouter } from "next/navigation";
import { createContext, use, useEffect, useMemo, useState, useTransition } from "react";
import {
	addToCartAction,
	removeCartLineAction,
	setCartQtyAction,
} from "@/app/[locale]/(shop)/cart/actions";
import { cartKey, lineKey } from "@/components/cart/cart-keys";
import { MAX_LINE_QTY } from "@/lib/commerce/cart-qty";
import { AddedToCartToast } from "./AddedToCartToast";
import { CartDrawer } from "./CartDrawer";

export interface AddonSelection {
	gi: number; // add-on group index
	ci: number; // choice index
	label: LocalizedText;
	price: number;
	/** The add-on group's name, for display only. */
	group?: LocalizedText;
}

export interface CartItem {
	productId: string;
	variantId?: string;
	variantLabel?: LocalizedText;
	variantOptions?: OrderItemVariantOptions;
	slug: string;
	name: LocalizedText;
	image?: string;
	price: number; // unit price INCLUDING selected add-ons
	addons?: AddonSelection[];
	qty: number;
	taxable?: boolean; // VAT applies; missing = true (older saved carts)
	/** Grams per unit, from the server; missing weighs nothing, as the charge does. */
	weightGrams?: number;
	note?: string; // optional per-item customer note
}

export interface CartFlash {
	id: number; // bumps on every add so listeners can re-trigger
	/**
	 * `refused` is a view-only store rejecting the add; `failed` is an add that
	 * never reached the cookie (a network error, or a tab older than the
	 * deploy). The toast MUST branch on these: rendering either through the
	 * success presentation would tell the customer the item was added.
	 */
	kind: "added" | "refused" | "failed";
	name: LocalizedText;
	qty: number;
}

interface CartContextValue {
	items: CartItem[];
	count: number;
	total: number;
	flash: CartFlash | null;
	/**
	 * Browse-only catalogue (`StoreSettings.catalogOnly`, or a store that is not
	 * live): ordering is disabled.
	 */
	viewOnly: boolean;
	/**
	 * `announce: false` adds the line without the success flash, so the cart
	 * drawer stays shut: it is for a caller that navigates to the cart itself
	 * (Buy Now), where the destination is the confirmation and a drawer would
	 * cover the page the customer asked for. A REFUSAL still flashes either way,
	 * because it is the only signal that nothing went in.
	 */
	add: (item: Omit<CartItem, "qty">, qty?: number, opts?: { announce?: boolean }) => void;
	setQty: (key: string, qty: number) => void;
	/** Update a line's unit price (used to accept a server-side price change). */
	updatePrice: (key: string, price: number) => void;
	remove: (key: string) => void;
	clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

/**
 * ## ⚠️ The cart is a COOKIE now, resolved on the server (TRY-128)
 *
 * It was `localStorage`, and that made a no-JavaScript order impossible at the root
 * rather than merely awkward: measured with `curl`, `/en/checkout` rendered "Your cart
 * is empty" and the checkout form was not in the HTML at all, because the cart only
 * existed in the browser. There was nothing for a form action to attach to.
 *
 * So this provider is no longer the owner of the cart. It is a fast local mirror:
 *
 * - `initialItems` arrives from the server, resolved from the cookie through
 *   `resolveCartLines` — so names, variant labels, add-on labels and **prices** are
 *   the database's, not a copy the browser has been carrying around.
 * - every mutation posts the matching server action, which writes the cookie and
 *   revalidates. The local update is optimistic, and the next server render is what
 *   makes it true.
 *
 * ⚠️ **`updatePrice` is retained and is now nearly pointless**, deliberately: the
 * server resolves prices on every render, so a "server-side price change" cannot go
 * unnoticed the way it could when the browser held the only copy. `CartView` still
 * calls it and the call is harmless — the next server render overwrites it. Do not
 * build anything new on it.
 *
 * ⚠️ **Cross-tab live sync is gone**, and it was real: a `storage` listener adopted
 * another tab's cart immediately. A cookie is shared, so both tabs agree on the next
 * navigation or revalidate, but a second tab sitting idle now shows a stale count
 * until it moves. That is the cost of the change, and it is smaller than a cart no
 * server can read.
 */
export function CartProvider({
	children,
	viewOnly = false,
	initialItems = [],
	currency,
	factor,
}: {
	children: ReactNode;
	viewOnly?: boolean;
	/** The cookie's cart, resolved server-side. See the docblock. */
	initialItems?: CartItem[];
	/**
	 * The customer's display currency, for the drawer's prices. Absent on the
	 * platform host, which has no store and so has no cart to price.
	 */
	currency?: CurrencyInfo;
	/** Base-to-display multiplier that goes with `currency`. */
	factor?: number;
}) {
	const [items, setItems] = useState<CartItem[]>(initialItems);
	const [hydrated, setHydrated] = useState(false);
	const [flash, setFlash] = useState<CartFlash | null>(null);
	const [, startTransition] = useTransition();
	const router = useRouter();

	// Adopt the server's cart whenever it changes — i.e. after an action's
	// revalidate. This is the direction that matters: an optimistic local edit is
	// replaced by the truth, never the other way round.
	useEffect(() => {
		setItems(initialItems);
		setHydrated(true);
	}, [initialItems]);

	// Persist the cart server-side (debounced) so a recovery reminder can be sent
	// if the customer doesn't check out. The endpoint no-ops for guests.
	useEffect(() => {
		if (!hydrated) return;
		// A browse-only store takes no orders, so there is no reminder to send and
		// no reason to post a cart the server would refuse anyway. This is an
		// optimisation, NOT the gate: /api/cart/track is a public endpoint and
		// refuses on its own.
		//
		// ⚠️ `items.length > 0` is load-bearing. The route keeps its
		// `items.length === 0` deleteMany AHEAD of the browse-only gate on purpose
		// — clearing a pending row is housekeeping, not tracking — and this is its
		// only caller, so a bare `if (viewOnly) return` made that branch
		// unreachable on exactly the stores it was preserved for. The path is real:
		// the store is live, a customer builds a cart and a row is written, the
		// merchant flips to browse-only, and the customer comes back and empties
		// it. `/cart` has no browse-only redirect and `setQty`/`remove`/`clear` are
		// ungated — only `add` is — so emptying it is an ordinary thing to do. With
		// no POST the row then survives to `abandonedDismissDays` (30 by default),
		// showing the merchant a cart the customer already discarded and counting
		// its total in the recovery-value aggregate.
		if (viewOnly && items.length > 0) return;
		const total = items.reduce((s, i) => s + i.price * i.qty, 0);
		const id = setTimeout(() => {
			fetch("/api/cart/track", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					items: items.map((i) => ({
						name: i.name,
						qty: i.qty,
						price: i.price,
						slug: i.slug,
					})),
					total,
				}),
				keepalive: true,
			}).catch(() => {});
		}, 2500);
		return () => clearTimeout(id);
	}, [items, hydrated, viewOnly]);

	const value = useMemo<CartContextValue>(() => {
		/**
		 * Post one mutation. The optimistic state is already applied by the caller.
		 *
		 * ⚠️ A rejected action is followed by a refresh, never left alone. Nothing
		 * else re-renders the layout on a soft navigation, so the optimistic line
		 * stayed in the drawer and on `/cart` while the cookie never held it, and
		 * vanished at checkout on the first server render there (signing in is one).
		 */
		function send(
			action: (fd: FormData) => Promise<unknown>,
			fields: Record<string, string>,
			onFail?: () => void,
		) {
			const fd = new FormData();
			for (const [k, v] of Object.entries(fields)) fd.set(k, v);
			startTransition(async () => {
				try {
					await action(fd);
				} catch {
					onFail?.();
					router.refresh();
				}
			});
		}

		const add: CartContextValue["add"] = (item, qty = 1, opts) => {
			if (viewOnly) {
				// View-only menu: ordering disabled. Flash rather than return
				// silently — a click that reaches here means some path forgot to
				// check `viewOnly`, and silence makes that path invisible.
				setFlash((f) => ({
					id: (f?.id ?? 0) + 1,
					kind: "refused",
					name: item.name,
					qty,
				}));
				return;
			}
			setItems((prev) => {
				const key = cartKey(item.productId, item.variantId, item.addons, item.note);
				const existing = prev.find((i) => lineKey(i) === key);
				if (existing) {
					return prev.map((i) =>
						lineKey(i) === key ? { ...i, qty: Math.min(MAX_LINE_QTY, i.qty + qty) } : i,
					);
				}
				return [...prev, { ...item, qty: Math.min(MAX_LINE_QTY, qty) }];
			});
			// ⚠️ Ids and quantity only — the same fields the cookie holds and the same
			// ones `parseItems` has always kept. Sending a price would invite the
			// server to trust one.
			send(
				addToCartAction,
				{
					productId: item.productId,
					...(item.variantId ? { variantId: item.variantId } : {}),
					qty: String(qty),
					...(item.addons && item.addons.length > 0
						? { addons: JSON.stringify(item.addons.map((a) => ({ gi: a.gi, ci: a.ci }))) }
						: {}),
					...(item.note ? { note: item.note } : {}),
				},
				() =>
					setFlash((f) => ({
						id: (f?.id ?? 0) + 1,
						kind: "failed",
						name: item.name,
						qty,
					})),
			);
			// Signal a transient "added to cart" flash, which is what opens the
			// drawer. A caller heading for the cart asks for silence instead.
			if (opts?.announce !== false) {
				setFlash((f) => ({
					id: (f?.id ?? 0) + 1,
					kind: "added",
					name: item.name,
					qty,
				}));
			}
		};

		const setQty: CartContextValue["setQty"] = (key, wanted) => {
			// Clamped here as well as on the server: checkout posts THESE items, and
			// one line past the cap fails the whole list there.
			const qty = Math.min(MAX_LINE_QTY, wanted);
			setItems((prev) =>
				qty <= 0
					? prev.filter((i) => lineKey(i) !== key)
					: prev.map((i) => (lineKey(i) === key ? { ...i, qty } : i)),
			);
			send(setCartQtyAction, { key, qty: String(qty) });
		};

		const updatePrice: CartContextValue["updatePrice"] = (key, price) =>
			setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, price } : i)));

		const remove: CartContextValue["remove"] = (key) => {
			setItems((prev) => prev.filter((i) => lineKey(i) !== key));
			send(removeCartLineAction, { key });
		};

		return {
			items,
			count: items.reduce((n, i) => n + i.qty, 0),
			total: items.reduce((s, i) => s + i.price * i.qty, 0),
			flash,
			viewOnly,
			add,
			setQty,
			updatePrice,
			remove,
			clear: () => {
				setItems([]);
				// Removing every line rather than a `clearCartAction` call: one code path
				// for "the cart shrank", and the cookie ends up empty either way.
				for (const i of items) send(removeCartLineAction, { key: lineKey(i) });
			},
		};
	}, [items, flash, viewOnly, router]);

	return (
		<CartContext value={value}>
			{/* Both mounted once for the whole storefront so they survive
          navigation (the old per-CartLink popup re-triggered on every remount).
          A successful add opens the drawer; only a refusal is still a toast.
          The drawer is a column beside the page, so it wraps the page. */}
			{currency ? (
				<CartDrawer currency={currency} factor={factor ?? 1}>
					{children}
				</CartDrawer>
			) : (
				children
			)}
			<AddedToCartToast />
		</CartContext>
	);
}

export function useCart(): CartContextValue {
	const ctx = use(CartContext);
	if (!ctx) throw new Error("useCart must be used within a CartProvider");
	return ctx;
}
